import { useState, useEffect } from 'react';
import DealCard from './DealCard';
import { categorise } from '../utils/categorise';
import { storeLink } from '../utils/storeLink';

const STORES = ['All', 'Coles', 'Woolworths', 'Aldi', 'Chemist Warehouse'];
const STORE_ORDER = ['Coles', 'Woolworths', 'Aldi', 'Chemist Warehouse'];

function groupByCategory(deals) {
  const groups = {};
  for (const deal of deals) {
    const cat = categorise(deal);
    if (!groups[cat]) groups[cat] = [];
    groups[cat].push(deal);
  }
  return Object.entries(groups).sort(([a], [b]) => {
    if (a === 'Other') return 1;
    if (b === 'Other') return -1;
    return a.localeCompare(b);
  });
}

// ---- Catalogue badge ----

function catalogueBadge(meta) {
  if (!meta?.validUntil) return null;
  const msLeft   = new Date(meta.validUntil) - new Date();
  const daysLeft = Math.ceil(msLeft / (1000 * 60 * 60 * 24));
  if (daysLeft < 0) return { label: 'Expired', cls: 'cat-badge--expired' };
  if (daysLeft <= 3) return { label: `Expires in ${daysLeft} day${daysLeft !== 1 ? 's' : ''}`, cls: 'cat-badge--soon' };
  return { label: `Valid until ${new Date(meta.validUntil).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}`, cls: 'cat-badge--ok' };
}

// ---- Component ----

function DealGrid({ deals, shoppingList, onAddToList, loading, message, catalogues = {}, favourites = [], onToggleFavourite }) {
  const [store, setStore]               = useState('All');
  const [activeCategory, setActiveCategory] = useState('All');
  const [search, setSearch]             = useState('');
  const [sortBy, setSortBy]             = useState('discount');
  const [collapsed, setCollapsed]       = useState({});
  const [collapseInit, setCollapseInit] = useState(false);
  const [viewMode, setViewMode]         = useState('grid');

  // Collapse all store sections except one by default, to reduce initial render.
  // Pick whichever store actually has deals first (by STORE_ORDER) rather than
  // hardcoding a store name — a store being temporarily empty (e.g. blocked
  // scrape) shouldn't leave every section collapsed with nothing visible.
  useEffect(() => {
    if (collapseInit || deals.length === 0) return;
    const firstWithDeals = STORE_ORDER.find(s => deals.some(d => d.store === s));
    setCollapsed(Object.fromEntries(
      STORE_ORDER.filter(s => s !== firstWithDeals).map(s => [`store:${s}`, true])
    ));
    setCollapseInit(true);
  }, [deals, collapseInit]);

  const listSet   = new Set(shoppingList.map(i => i.name + i.store));
  const favSet    = new Set(favourites.map(f => f.name + f.store));
  const searching = search.trim().length > 0;

  function selectStore(s) {
    setStore(s);
    setActiveCategory('All');
  }

  function toggleCollapse(key) {
    setCollapsed(prev => ({ ...prev, [key]: !prev[key] }));
  }

  // Sort + filter
  let filtered = deals;
  if (store !== 'All') filtered = filtered.filter(d => d.store === store);
  if (searching) {
    const q = search.toLowerCase();
    filtered = filtered.filter(d => d.name.toLowerCase().includes(q));
  }
  filtered = [...filtered].sort((a, b) => {
    if (sortBy === 'discount') return (b.discount || 0) - (a.discount || 0);
    if (sortBy === 'savings') {
      const sa = a.originalPrice ? a.originalPrice - a.salePrice : 0;
      const sb = b.originalPrice ? b.originalPrice - b.salePrice : 0;
      return sb - sa;
    }
    if (sortBy === 'price') return a.salePrice - b.salePrice;
    return 0;
  });

  // Apply active category filter (single-store view)
  const displayDeals = store !== 'All' && activeCategory !== 'All'
    ? filtered.filter(d => categorise(d) === activeCategory)
    : filtered;

  // Counts reflect current search so pills update as user types
  const searchQ = search.trim().toLowerCase();
  const searchFiltered = searchQ
    ? deals.filter(d => d.name.toLowerCase().includes(searchQ))
    : deals;
  const counts = STORES.reduce((acc, s) => {
    acc[s] = s === 'All' ? searchFiltered.length : searchFiltered.filter(d => d.store === s).length;
    return acc;
  }, {});

  // Category chips for single-store view
  const storeCategoryGroups = store !== 'All' && !searching && filtered.length > 10
    ? groupByCategory(filtered)
    : null;

  // ---- render helpers ----

  function renderGrid(items) {
    return (
      <div className="deal-grid">
        {items.map((deal, i) => (
          <DealCard
            key={`${deal.store}-${deal.name}-${i}`}
            deal={deal}
            onAddToList={onAddToList}
            inList={listSet.has(deal.name + deal.store)}
            isFavourited={favSet.has(deal.name + deal.store)}
            onToggleFavourite={onToggleFavourite}
          />
        ))}
      </div>
    );
  }

  function renderTable(items) {
    return (
      <div className="deal-table">
        <div className="deal-table-header">
          <span>Store</span>
          <span>Product</span>
          <span className="dt-col-price">Sale price</span>
          <span className="dt-col-was">Was</span>
          <span className="dt-col-save">Saving</span>
          <span className="dt-col-actions" />
        </div>
        {items.map((deal, i) => {
          const inList     = listSet.has(deal.name + deal.store);
          const isFav      = favSet.has(deal.name + deal.store);
          const saving     = deal.originalPrice ? (deal.originalPrice - deal.salePrice).toFixed(2) : null;
          return (
            <div key={`${deal.store}-${deal.name}-${i}`} className="deal-table-row">
              <span className="dt-store">
                <span className="shopping-item-store-badge" data-store={deal.store}>{deal.store}</span>
              </span>
              <span className="dt-name">
                {deal.discount && <span className="dt-discount">{deal.discount}% off</span>}
                <span className="dt-name-text" title={deal.name}>{deal.name}</span>
              </span>
              <span className="dt-col-price dt-price">${deal.salePrice.toFixed(2)}</span>
              <span className="dt-col-was dt-was">
                {deal.originalPrice ? `$${deal.originalPrice.toFixed(2)}` : '—'}
              </span>
              <span className="dt-col-save dt-save">
                {saving ? `$${saving}` : '—'}
              </span>
              <span className="dt-col-actions dt-actions">
                {onToggleFavourite && (
                  <button
                    className={`dt-fav-btn ${isFav ? 'dt-fav-btn--on' : ''}`}
                    onClick={() => onToggleFavourite(deal)}
                    title={isFav ? 'Remove from favourites — currently watching this item' : 'Save to favourites — get notified when on sale again'}
                    aria-label={isFav ? 'Remove from favourites' : 'Save to favourites'}
                  >{isFav ? '♥' : '♡'}</button>
                )}
                <button
                  className={`dt-add-btn ${inList ? 'dt-add-btn--in' : ''}`}
                  onClick={() => !inList && onAddToList(deal)}
                  disabled={inList}
                  title={inList ? 'Already in your shopping list' : 'Add to your shopping list'}
                >
                  <span className="dt-btn-icon">{inList ? '✓' : '+'}</span>
                  <span className="dt-btn-label">{inList ? ' In list' : ' Add to list'}</span>
                </button>
                {(() => { const link = deal.url || storeLink(deal); return link ? (
                  <a
                    href={link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="dt-view-btn"
                    title={`View on ${deal.store} website`}
                  >
                    <span className="dt-btn-icon">↗</span>
                    <span className="dt-btn-label"> View</span>
                  </a>
                ) : null; })()}
              </span>
            </div>
          );
        })}
      </div>
    );
  }

  function renderItems(items) {
    return viewMode === 'table' ? renderTable(items) : renderGrid(items);
  }

  function CollapsibleSection({ collapseKey, header, count, children }) {
    const isCollapsed = collapsed[collapseKey];
    return (
      <div className="deal-category-section">
        <button
          className="deal-category-header"
          onClick={() => toggleCollapse(collapseKey)}
          aria-expanded={!isCollapsed}
        >
          <span className="deal-category-chevron">{isCollapsed ? '▸' : '▾'}</span>
          <span className="deal-category-name">{header}</span>
          <span className="deal-category-count">{count}</span>
        </button>
        {!isCollapsed && <div className="deal-category-body">{children}</div>}
      </div>
    );
  }

  function renderCategoryGroups(items, keyPrefix) {
    if (items.length <= 10 || searching) return renderItems(items);
    return groupByCategory(items).map(([cat, catItems]) => (
      <CollapsibleSection
        key={cat}
        collapseKey={`${keyPrefix}:${cat}`}
        header={cat}
        count={catItems.length}
      >
        {renderItems(catItems)}
      </CollapsibleSection>
    ));
  }

  // All view: collapsible store sections, each with collapsible category groups
  const byStore = store === 'All' && !searching
    ? STORE_ORDER.map(s => [s, filtered.filter(d => d.store === s)]).filter(([, items]) => items.length > 0)
    : null;

  return (
    <div className="deal-grid-container">

      {/* Controls: search → store filters → sort/view */}
      <div className="deal-controls">
        <input
          type="text"
          className="deal-search"
          placeholder="Search products…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <div className="deal-filters-row">
          <div className="store-filters">
            {STORES.map(s => (
              <button
                key={s}
                data-store={s}
                className={`store-filter-btn ${store === s ? 'store-filter-btn--active' : ''}`}
                onClick={() => selectStore(s)}
              >
                {s}
                <span className="store-filter-count">{counts[s]}</span>
              </button>
            ))}
          </div>
          <div className="deal-sort-row">
          <select
            className="deal-sort"
            value={sortBy}
            onChange={e => setSortBy(e.target.value)}
          >
            <option value="discount">Sort: Best % Off</option>
            <option value="savings">Sort: Most Savings</option>
            <option value="price">Sort: Lowest Price</option>
          </select>
          <div className="view-toggle" role="group" aria-label="View mode">
            <button
              className={`view-toggle-btn ${viewMode === 'grid' ? 'view-toggle-btn--active' : ''}`}
              onClick={() => setViewMode('grid')}
              title="Grid view — show deals as cards"
              aria-pressed={viewMode === 'grid'}
            >
              <span className="view-icon-grid"><span/><span/><span/><span/></span>
              <span className="view-toggle-label">Grid</span>
            </button>
            <button
              className={`view-toggle-btn ${viewMode === 'table' ? 'view-toggle-btn--active' : ''}`}
              onClick={() => setViewMode('table')}
              title="List view — show deals in a compact table"
              aria-pressed={viewMode === 'table'}
            >
              <span className="view-icon-list"><span/><span/><span/></span>
              <span className="view-toggle-label">List</span>
            </button>
          </div>
        </div>
        </div>
      </div>

      {/* Category filter chips — shown for single-store view with >10 deals */}
      {storeCategoryGroups && (
        <div className="category-chips">
          <button
            className={`category-chip ${activeCategory === 'All' ? 'category-chip--active' : ''}`}
            onClick={() => setActiveCategory('All')}
          >
            All <span className="category-chip-count">{filtered.length}</span>
          </button>
          {storeCategoryGroups.map(([cat, items]) => (
            <button
              key={cat}
              className={`category-chip ${activeCategory === cat ? 'category-chip--active' : ''}`}
              onClick={() => setActiveCategory(cat)}
            >
              {cat} <span className="category-chip-count">{items.length}</span>
            </button>
          ))}
        </div>
      )}

      {/* Catalogue info banner */}
      {['Chemist Warehouse', 'All'].includes(store) && catalogues.chemistWarehouse && (() => {
        const meta  = catalogues.chemistWarehouse;
        const badge = catalogueBadge(meta);
        return (
          <div className="catalogue-info">
            <span className="catalogue-info-title">{meta.title}</span>
            {badge && <span className={`cat-badge ${badge.cls}`}>{badge.label}</span>}
          </div>
        );
      })()}

      {loading && (
        <div className="deal-state-msg">
          <div className="spinner" />
          <p>Loading deals…</p>
        </div>
      )}
      {!loading && message && <div className="deal-state-msg"><p>{message}</p></div>}
      {!loading && !message && displayDeals.length === 0 && (
        <div className="deal-state-msg"><p>No deals match your filters.</p></div>
      )}

      {!loading && displayDeals.length > 0 && (
        <>
          <p className="deal-count">{displayDeals.length} deal{displayDeals.length !== 1 ? 's' : ''} found</p>

          {/* All view: store → category hierarchy */}
          {byStore ? (
            byStore.map(([storeName, storeItems]) => (
              <div key={storeName} className="deal-store-section">
                <button
                  className="deal-store-header"
                  onClick={() => toggleCollapse(`store:${storeName}`)}
                  aria-expanded={!collapsed[`store:${storeName}`]}
                >
                  <span className="deal-store-chevron">
                    {collapsed[`store:${storeName}`] ? '▸' : '▾'}
                  </span>
                  <span className="deal-store-name">{storeName}</span>
                  <span className="deal-category-count">{storeItems.length}</span>
                </button>
                {!collapsed[`store:${storeName}`] && (
                  <div className="deal-store-body">
                    {renderCategoryGroups(storeItems, `cat:${storeName}`)}
                  </div>
                )}
              </div>
            ))
          ) : activeCategory !== 'All' ? (
            // Single category selected — flat list
            renderItems(displayDeals)
          ) : (
            // Single store, all categories — collapsible category sections
            renderCategoryGroups(displayDeals, `cat:${store}`)
          )}
        </>
      )}
    </div>
  );
}

export default DealGrid;
